import { describeError } from '@/lib/form/errorMessages';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { ChevronLeft } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Permission,
  schemas,
  type CatalogItemTypeValue,
  type CreateCatalogItemRequest,
  type UpdateCatalogItemRequest,
} from '@kreart/shared';
import { Button } from '@/components/ui/button';
import { Form, TextField, SelectField, FormActions, FormErrorBanner } from '@/components/form';
import { useAuth } from '@/features/auth/useAuth';
import {
  useCatalogItem,
  useCreateCatalogItem,
  useDeleteCatalogItem,
  useUpdateCatalogItem,
} from '@/features/catalog/hooks';
import { errorKeyFor } from '@/features/catalog/errorMessages';
import { CatalogDangerZone } from '@/features/catalog/components/CatalogDangerZone';

interface FormValues {
  name: string;
  offerName: string;
  sku: string;
  description: string;
  defaultWidthMm: string;
  defaultHeightMm: string;
  defaultDepthMm: string;
  material: string;
  color: string;
  defaultCostNet: string;
  currency: 'RON' | 'EUR' | 'USD';
}

const EMPTY: FormValues = {
  name: '',
  offerName: '',
  sku: '',
  description: '',
  defaultWidthMm: '',
  defaultHeightMm: '',
  defaultDepthMm: '',
  material: '',
  color: '',
  defaultCostNet: '',
  currency: 'RON',
};

function toNum(v: string): number | undefined {
  if (v.trim() === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

interface Props {
  type: CatalogItemTypeValue;
  i18nNs: string;
  mode: 'create' | 'edit';
  // Routing strings; bound by the per-type route file.
  listTo:
    | '/cabinets'
    | '/panels'
    | '/countertops'
    | '/drawers'
    | '/elements'
    | '/lighting'
    | '/hardware';
  editParentRouteId:
    | '/protected/cabinets/$id'
    | '/protected/panels/$id'
    | '/protected/countertops/$id'
    | '/protected/drawers/$id'
    | '/protected/elements/$id'
    | '/protected/lighting/$id'
    | '/protected/hardware/$id';
}

export function CatalogItemFormPage(props: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const params = useParams({ strict: false }) as { id?: string };
  const id = props.mode === 'edit' ? params.id : undefined;

  const can = (p: string) => user?.permissions.includes(p) ?? false;
  const canUpdate = can(Permission.CATALOG_UPDATE);
  const canDelete = can(Permission.CATALOG_DELETE);
  const canReadCost = can(Permission.CATALOG_READ_COST);

  const itemQuery = useCatalogItem(id);
  const createMut = useCreateCatalogItem();
  const updateMut = useUpdateCatalogItem(id ?? '');
  const deleteMut = useDeleteCatalogItem(id ?? '');

  const form = useForm<FormValues>({
    resolver: zodResolver(
      props.mode === 'create'
        ? schemas.createCatalogItemRequest.omit({ type: true })
        : schemas.updateCatalogItemRequest,
    ),
    defaultValues: EMPTY,
  });

  // Hydrate the form once the item loads (edit mode).
  useEffect(() => {
    if (props.mode !== 'edit' || !itemQuery.data) return;
    const it = itemQuery.data;
    form.reset({
      name: it.name,
      offerName: it.offerName ?? '',
      sku: it.sku ?? '',
      description: it.description ?? '',
      defaultWidthMm: it.defaultWidthMm !== null ? String(it.defaultWidthMm) : '',
      defaultHeightMm: it.defaultHeightMm !== null ? String(it.defaultHeightMm) : '',
      defaultDepthMm: it.defaultDepthMm !== null ? String(it.defaultDepthMm) : '',
      material: it.material ?? '',
      color: it.color ?? '',
      defaultCostNet: it.defaultCostNet ?? '',
      currency: it.currency,
    });
  }, [itemQuery.data, props.mode, form]);

  const isReadOnly = props.mode === 'edit' && !canUpdate;

  const buildBody = (values: FormValues): CreateCatalogItemRequest | UpdateCatalogItemRequest => {
    const base = {
      name: values.name,
      offerName: values.offerName || undefined,
      sku: values.sku || undefined,
      description: values.description || undefined,
      defaultWidthMm: toNum(values.defaultWidthMm) ?? null,
      defaultHeightMm: toNum(values.defaultHeightMm) ?? null,
      defaultDepthMm: toNum(values.defaultDepthMm) ?? null,
      material: values.material || undefined,
      color: values.color || undefined,
      currency: values.currency,
      ...(canReadCost && values.defaultCostNet ? { defaultCostNet: values.defaultCostNet } : {}),
    };
    if (props.mode === 'create') {
      return { type: props.type, ...base } as CreateCatalogItemRequest;
    }
    return base as UpdateCatalogItemRequest;
  };

  const onSubmit = async (values: FormValues) => {
    try {
      if (props.mode === 'create') {
        const created = await createMut.mutateAsync(buildBody(values) as CreateCatalogItemRequest);
        void navigate({ to: props.listTo, search: {} });
        return created;
      }
      if (!id) return;
      await updateMut.mutateAsync(buildBody(values) as UpdateCatalogItemRequest);
    } catch {
      /* surfaced via mutation errors */
    }
  };

  const onDelete = async () => {
    if (!id) return;
    try {
      await deleteMut.mutateAsync();
      void navigate({ to: props.listTo, search: {} });
    } catch {
      /* surfaced via deleteMut.error */
    }
  };

  const submitting = props.mode === 'create' ? createMut.isPending : updateMut.isPending;
  const submitError = props.mode === 'create' ? createMut.error : updateMut.error;

  if (props.mode === 'edit' && itemQuery.isLoading) {
    return <div className="text-sm text-muted-foreground">{t('common.loading')}</div>;
  }
  if (props.mode === 'edit' && itemQuery.isError) {
    return (
      <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
        {describeError(t, itemQuery.error, errorKeyFor)}
      </div>
    );
  }

  const titleKey =
    props.mode === 'create' ? `${props.i18nNs}.create.title` : `${props.i18nNs}.edit.title`;

  return (
    <div className="w-full space-y-4">
      <Link
        to={props.listTo}
        search={{}}
        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" /> {t(`${props.i18nNs}.backToList`)}
      </Link>
      <h1 className="text-2xl font-semibold text-foreground">{t(titleKey)}</h1>

      <Form
        form={form}
        onSubmit={onSubmit}
        className="space-y-4 rounded-md border border-border bg-card p-4"
      >
        <fieldset disabled={isReadOnly} className="space-y-4">
          <TextField name="name" label={t('catalog.fields.name')} autoFocus />
          <TextField
            name="offerName"
            label={t('catalog.offerName', { defaultValue: 'Offer Name' })}
          />
          <div className="grid grid-cols-2 gap-3">
            <TextField name="sku" label={t('catalog.fields.sku')} />
            <TextField name="material" label={t('catalog.fields.material')} />
          </div>
          <TextField name="description" label={t('catalog.fields.description')} />

          <div className="grid grid-cols-3 gap-3">
            <TextField
              name="defaultWidthMm"
              type="number"
              label={t('catalog.fields.widthMm')}
              min={0}
            />
            <TextField
              name="defaultHeightMm"
              type="number"
              label={t('catalog.fields.heightMm')}
              min={0}
            />
            <TextField
              name="defaultDepthMm"
              type="number"
              label={t('catalog.fields.depthMm')}
              min={0}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <TextField name="color" label={t('catalog.fields.color')} />
            {canReadCost && (
              <div className="grid grid-cols-[1fr_auto] gap-2">
                <TextField
                  name="defaultCostNet"
                  label={t('catalog.fields.costNet')}
                  inputMode="decimal"
                  placeholder="0.00"
                />
                <SelectField
                  name="currency"
                  label={t('catalog.fields.currency')}
                  options={[
                    { value: 'RON', label: 'RON' },
                    { value: 'EUR', label: 'EUR' },
                    { value: 'USD', label: 'USD' },
                  ]}
                />
              </div>
            )}
          </div>
        </fieldset>

        <FormErrorBanner error={submitError} resolveKey={errorKeyFor} />

        {!isReadOnly && (
          <FormActions
            submitLabel={t(
              props.mode === 'create' ? `${props.i18nNs}.create.submit` : 'common.save',
            )}
            isSubmitting={submitting}
            submitDisabled={!form.formState.isDirty && props.mode === 'edit'}
          >
            <Link to={props.listTo} search={{}}>
              <Button type="button" variant="ghost">
                {t('common.cancel')}
              </Button>
            </Link>
          </FormActions>
        )}
      </Form>

      {props.mode === 'edit' && canDelete && itemQuery.data && (
        <CatalogDangerZone
          itemName={itemQuery.data.name}
          pending={deleteMut.isPending}
          error={deleteMut.error}
          onConfirm={onDelete}
        />
      )}
    </div>
  );
}
